// ============================================================
// NotificationsCenter — the V2 Notifications surface ("Scheduled" → this).
//
// Visual language adapted from the operator's Quam notification design
// (notifications-live.jsx): grouped channel columns, animated SVG channel
// icons, color-coded lanes, recent/urgent badges, staggered fadeSlideIn,
// live pulse. Mapped to REAL Square Code lanes:
//   Messages · Tasks · Calls (Daily) · Daily Meetings
//
// Honesty: every item arrives from a live backend via useNotificationsFeed —
// zero seeded rows, zero fabricated counts. Per-column loading / auth /
// error / empty states are real. "Mark messages read" performs the REAL
// read-cursor write and reports failure honestly. No emoji — SVG icons only.
// ============================================================

import { useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import CommandCenterShell, { CC_INK, CC_MONO } from '../shell/CommandCenterShell'
import { MessageIcon, TaskIcon, MeetingIcon, CalendarIcon, OpenIcon, CheckIcon } from '../shell/icons'
import { relTime, type NotificationsFeedState, type NotifChannelKey, type NotifItem } from './notificationsData'

const hexAlpha = (hex: string, alpha: number) => {
  const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

const LANES: Array<{ key: NotifChannelKey; label: string; color: string; Icon: typeof MessageIcon; hint: string }> = [
  { key: 'messages', label: 'Messages', color: '#7c3aed', Icon: MessageIcon, hint: 'Channels + DMs (live comms spine)' },
  { key: 'tasks', label: 'Tasks', color: '#f59e0b', Icon: TaskIcon, hint: 'Open + blocked team tasks (live)' },
  { key: 'calls', label: 'Calls', color: '#059669', Icon: MeetingIcon, hint: 'Daily conference calls + provider status' },
  { key: 'meetings', label: 'Daily Meetings', color: '#3b82f6', Icon: CalendarIcon, hint: 'Internal daily meeting records (live)' },
]

export interface NotificationsCenterProps {
  feed: NotificationsFeedState
  onOpen?: (to: string) => void
  onReload?: () => void
  onMarkMessagesRead?: () => void
  markState?: 'idle' | 'working' | 'done' | 'failed'
  markableChannels?: number
  onHome?: () => void
  onBackCommandCenter?: () => void
  /** Injected clock for SSR-stable rendering; defaults to Date.now() in the route wrapper. */
  now?: number
}

function ItemRow({ item, color, onOpen, now }: { item: NotifItem; color: string; onOpen?: (to: string) => void; now: number }) {
  const [hov, setHov] = useState(false)
  return (
    <button
      type="button"
      onClick={() => onOpen?.(item.to)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10, width: '100%', textAlign: 'left', padding: '10px 12px',
        borderRadius: 11, cursor: 'pointer', transition: 'all .2s ease',
        background: hov ? hexAlpha(color, 0.06) : 'rgba(255,255,255,0.015)',
        border: `1px solid ${hov ? hexAlpha(color, 0.14) : 'rgba(255,255,255,0.03)'}`,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: CC_INK.primary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{item.title}</span>
          {item.recent && <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, boxShadow: `0 0 8px ${hexAlpha(color, 0.5)}`, flexShrink: 0 }} title="Recent — real timestamp under 24h" />}
          {item.urgent && (
            <span style={{ fontSize: 8.5, padding: '2px 7px', borderRadius: 6, background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.2)', color: '#f87171', fontWeight: 800, letterSpacing: '0.05em', flexShrink: 0 }}>URGENT</span>
          )}
        </div>
        <div style={{ fontSize: 11, color: CC_INK.muted, marginTop: 3, lineHeight: 1.35, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.preview}</div>
        {item.at && <div style={{ fontSize: 9.5, color: CC_INK.faint, marginTop: 3, fontFamily: CC_MONO }}>{relTime(item.at, now)}</div>}
      </div>
      <span style={{ opacity: hov ? 0.9 : 0.35, marginTop: 2 }}><OpenIcon size={11} color={color} /></span>
    </button>
  )
}

export default function NotificationsCenter({
  feed, onOpen, onReload, onMarkMessagesRead, markState = 'idle', markableChannels = 0, onHome, onBackCommandCenter, now,
}: NotificationsCenterProps) {
  const clock = now ?? Date.now()
  const totals = useMemo(() => {
    const all = LANES.flatMap((l) => feed[l.key].items)
    return { recent: all.filter((i) => i.recent).length, urgent: all.filter((i) => i.urgent).length }
  }, [feed])

  const laneBody = (key: NotifChannelKey, color: string) => {
    const col = feed[key]
    const empty: CSSProperties = { fontSize: 11.5, color: CC_INK.faint, fontStyle: 'italic', textAlign: 'center', padding: '18px 8px' }
    if (col.loading) return <div style={empty}>Loading from the live backend…</div>
    if (col.authRequired) return <div style={empty}>Sign in to the live cockpit to load this lane.</div>
    if (col.error) return <div style={{ ...empty, color: '#f87171', fontStyle: 'normal' }}>{col.error}</div>
    if (col.items.length === 0) return <div style={empty}>Nothing here yet — this lane is live and genuinely empty.</div>
    return col.items.map((item, i) => (
      <div key={item.id} style={{ opacity: 0, animation: `sqv2FadeSlideIn .3s ease ${120 + i * 45}ms forwards` }}>
        <ItemRow item={item} color={color} onOpen={onOpen} now={clock} />
      </div>
    ))
  }

  return (
    <CommandCenterShell
      onHome={onHome}
      onBackCommandCenter={onBackCommandCenter}
      breadcrumb={['COMMAND CENTER', 'NOTIFICATIONS']}
      title="Notifications"
      titleAccent="#0891b2"
      honestyBadge={{
        label: 'LIVE FEED · NOT FAKED',
        title: 'Every item is a real record from the live comms / tasks / calls / meetings backends. No seeded rows, no fabricated counts or urgency. A lane that fails to load says so.',
      }}
      contextBar={
        <>
          <span style={{ fontSize: 13, fontWeight: 700, color: CC_INK.primary }}>What needs attention</span>
          {totals.urgent > 0 && (
            <span style={{ fontSize: 10.5, padding: '4px 11px', borderRadius: 14, background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.16)', color: '#f87171', fontWeight: 700 }}>{totals.urgent} urgent</span>
          )}
          <span style={{ fontSize: 10.5, padding: '4px 11px', borderRadius: 14, background: 'rgba(8,145,178,0.07)', border: '1px solid rgba(8,145,178,0.14)', color: '#0891b2', fontWeight: 600 }}>{totals.recent} recent</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginLeft: 4 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#0891b2', boxShadow: '0 0 8px rgba(8,145,178,0.6)', animation: 'sqv2LivePulse 2s ease-in-out infinite' }} />
            <span style={{ fontSize: 10, color: CC_INK.faint, fontWeight: 500 }}>Live</span>
          </span>
          <span style={{ flex: 1 }} />
          {onReload && <button type="button" onClick={onReload} style={{ padding: '5px 12px', borderRadius: 8, cursor: 'pointer', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: CC_INK.secondary, fontSize: 11, fontWeight: 700 }}>Refresh</button>}
        </>
      }
    >
      <style>{`
        @keyframes sqv2FadeSlideIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes sqv2LivePulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.4; transform: scale(1.5); } }
      `}</style>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12 }}>
        {LANES.map((lane, i) => {
          const col = feed[lane.key]
          const recentCount = col.items.filter((it) => it.recent).length
          return (
            <section key={lane.key} style={{ background: 'rgba(255,255,255,0.018)', border: '1px solid rgba(255,255,255,0.04)', borderRadius: 16, overflow: 'hidden', opacity: 0, animation: `sqv2FadeSlideIn .5s ease ${i * 80}ms forwards` }}>
              <header style={{ padding: '14px 14px 12px', borderBottom: `1px solid ${hexAlpha(lane.color, 0.08)}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ width: 36, height: 36, borderRadius: 10, background: hexAlpha(lane.color, 0.08), border: `1px solid ${hexAlpha(lane.color, 0.14)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 0 12px ${hexAlpha(lane.color, 0.06)}` }}>
                    <lane.Icon color={lane.color} size={19} />
                  </span>
                  <span>
                    <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: lane.color, letterSpacing: '-0.02em' }}>{lane.label}</span>
                    <span style={{ display: 'block', fontSize: 9.5, color: CC_INK.faint, marginTop: 1 }} title={lane.hint}>{col.loading ? 'loading' : `${col.items.length} shown`}</span>
                  </span>
                </div>
                {recentCount > 0 && (
                  <span style={{ padding: '3px 9px', borderRadius: 12, background: hexAlpha(lane.color, 0.08), border: `1px solid ${hexAlpha(lane.color, 0.15)}`, fontSize: 10, color: lane.color, fontWeight: 700 }}>{recentCount} recent</span>
                )}
              </header>
              <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 4, minHeight: 90 }}>
                {laneBody(lane.key, lane.color)}
              </div>
              {lane.key === 'calls' && feed.calls.dailyStatus && (
                <div style={{ padding: '8px 14px 12px', fontSize: 9.5, fontFamily: CC_MONO, color: feed.calls.dailyStatus.meeting_mode === 'ready_for_internal_test' ? '#3d9a6c' : '#b8862a' }}>
                  provider: {feed.calls.dailyStatus.meeting_mode}
                </div>
              )}
            </section>
          )
        })}
      </div>

      <footer style={{ marginTop: 16, padding: '13px 18px', borderRadius: 14, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
        {LANES.map((lane) => (
          <span key={lane.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: lane.color, boxShadow: `0 0 6px ${hexAlpha(lane.color, 0.35)}` }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: lane.color }}>{feed[lane.key].items.length}</span>
            <span style={{ fontSize: 10.5, color: CC_INK.faint, fontWeight: 500 }}>{lane.label}</span>
          </span>
        ))}
        <span style={{ flex: 1 }} />
        {onMarkMessagesRead && (
          <button
            type="button"
            onClick={onMarkMessagesRead}
            disabled={markState === 'working' || markableChannels === 0}
            title={markableChannels === 0 ? 'No channels loaded to mark read.' : `Writes the REAL read cursor (last_read_at) on ${markableChannels} channel(s)/DM(s). Tasks / calls / meetings have no read state — no fake mark-read is offered for them.`}
            style={{
              padding: '8px 16px', borderRadius: 10, cursor: markableChannels === 0 ? 'not-allowed' : 'pointer',
              background: 'linear-gradient(135deg, rgba(124,58,237,0.25) 0%, rgba(8,145,178,0.25) 100%)',
              border: '1px solid rgba(8,145,178,0.15)', fontSize: 11.5, color: '#0891b2', fontWeight: 600, opacity: markableChannels === 0 ? 0.6 : 1,
            }}
          >
            {markState === 'working' ? 'Marking…' : markState === 'done' ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><CheckIcon size={12} color="#3d9a6c" /> Messages marked read</span> : markState === 'failed' ? 'Mark read failed — retry' : 'Mark messages read'}
          </button>
        )}
      </footer>
    </CommandCenterShell>
  )
}
